import { type ReactNode, useMemo } from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useGetPermissionsMatrixQuery } from '../store/api';
import { practicePermissionCopy } from '../utils/practicePermissionCopy';

import { SectionCardTitle } from './SectionCardTitle';

interface RolePermissionsTableProps {
  title?: string;
  titleControl?: ReactNode;
  onClose?: () => void;
}

/**
 * Read-only reference of what each role can do in the organization — the full
 * canonical permission catalog rendered as checkmarks, with org overrides
 * applied. This is informational only; the editable org-override matrix is a
 * separate component (PermissionsMatrix).
 */
export function RolePermissionsTable({
  title = 'Role Permissions',
  onClose,
  titleControl,
}: RolePermissionsTableProps = {}) {
  const subject = useSubjectLanguageVariants();
  const copy = (text: string) => practicePermissionCopy(text, subject);
  const { data, isLoading, isError } = useGetPermissionsMatrixQuery();

  const permissions = useMemo(() => data?.permissions ?? [], [data?.permissions]);
  const headerRoles = permissions[0]?.roles ?? [];

  if (isLoading) {
    return (
      <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Loading permissions...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
      <CardHeader
        avatar={<TuneIcon color="primary" />}
        title={<SectionCardTitle title={title} control={titleControl} />}
        slotProps={{ title: { variant: 'h6', fontWeight: 600 } }}
        subheader="What each role can do in your practice."
        action={
          onClose && (
            <IconButton aria-label="close" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )
        }
      />

      <CardContent sx={{ pt: 0 }}>
        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Permissions could not be loaded.
          </Alert>
        )}

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 720, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '26%' }} />
              {headerRoles.map(role => (
                <col key={role.role} style={{ width: `${74 / (headerRoles.length || 1)}%` }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Permission</TableCell>
                {headerRoles.map(role => (
                  <TableCell key={role.role} align="center" sx={{ fontWeight: 600 }}>
                    {role.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions.map(permission => (
                <TableRow key={permission.permission}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {copy(permission.label)}
                    </Typography>
                    {permission.description && (
                      <Typography variant="caption" color="text.secondary">
                        {copy(permission.description)}
                      </Typography>
                    )}
                  </TableCell>
                  {(permission.roles ?? []).map(role => (
                    <TableCell
                      key={role.role}
                      align="center"
                      aria-label={`${copy(permission.label)} for ${role.label}: ${
                        role.enabled ? 'granted' : 'not granted'
                      }`}
                    >
                      {role.enabled ? (
                        <CheckIcon color="success" fontSize="small" />
                      ) : (
                        <RemoveIcon color="disabled" fontSize="small" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}
